import React from 'react';
import { Box, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { FaRegCalendarAlt, FaRegClock, FaExclamationCircle } from 'react-icons/fa';
import admissionDefault from '../../../../data/admission/admission.json';

const NAVY = '#0e2455';
const ORANGE = '#F6872A';

// Icon key → component.
const TIER_ICONS = { calendar: FaRegCalendarAlt, clock: FaRegClock, alert: FaExclamationCircle };

const VARIANTS = {
    orange: {
        bg: 'linear-gradient(135deg, #F6872A 0%, #ef7a18 100%)',
        color: '#ffffff',
        border: 'none',
        shadow: '0 26px 50px -26px rgba(246,135,42,0.6)',
        iconBg: 'rgba(255,255,255,0.18)', iconColor: '#ffffff',
        label: 'rgba(255,255,255,0.85)',
        ringTrack: 'rgba(255,255,255,0.28)', ringColor: '#ffffff', ringText: '#ffffff', ringSub: 'rgba(255,255,255,0.85)',
    },
    navy: {
        bg: 'linear-gradient(135deg, #0e2455 0%, #1a3673 100%)',
        color: '#ffffff',
        border: 'none',
        shadow: '0 26px 50px -26px rgba(14,36,85,0.65)',
        iconBg: 'rgba(255,255,255,0.14)', iconColor: '#ffffff',
        label: 'rgba(255,255,255,0.7)',
        ringTrack: 'rgba(255,255,255,0.2)', ringColor: '#ffffff', ringText: '#ffffff', ringSub: 'rgba(255,255,255,0.8)',
    },
    light: {
        bg: '#ffffff',
        color: NAVY,
        border: '1px solid #E8EDF5',
        shadow: '0 18px 40px -28px rgba(14,36,85,0.25)',
        iconBg: 'rgba(246,135,42,0.12)', iconColor: ORANGE,
        label: ORANGE,
        ringTrack: '#E8EDF5', ringColor: '#cbd5e1', ringText: NAVY, ringSub: '#94a3b8',
    },
};

const RefundRing = ({ percent, v }) => {
    const size = 108;
    const r = 42;
    const cx = size / 2;
    const c = 2 * Math.PI * r;
    const offset = c * (1 - percent / 100);
    return (
        <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                <circle cx={cx} cy={cx} r={r} fill="none" stroke={v.ringTrack} strokeWidth="5" />
                <motion.circle
                    cx={cx} cy={cx} r={r} fill="none"
                    stroke={v.ringColor} strokeWidth="5" strokeLinecap="round"
                    strokeDasharray={c}
                    transform={`rotate(-90 ${cx} ${cx})`}
                    initial={{ strokeDashoffset: c }}
                    whileInView={{ strokeDashoffset: offset }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.1, ease: 'easeOut', delay: 0.2 }}
                />
            </svg>
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', lineHeight: 1 }}>
                <Typography sx={{ fontWeight: 900, fontSize: '10px', lineHeight: 1, color: v.ringText }}>{percent}%</Typography>
                <Typography sx={{ fontSize: '8px', fontWeight: 800, letterSpacing: '0.02em', color: v.ringSub, mt: '3px' }}>REFUND</Typography>
            </Box>
        </Box>
    );
};

const AdmissionRefundPolicy = ({ data }) => {
    const d = data || admissionDefault.refund;
    const tiers = d.tiers || [];
    const points = d.points || [];

    const container = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.12 } } };
    const item = { hidden: { opacity: 0, y: 22 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } } };

    return (
        <Box sx={{ mb: 6 }}>
            {/* Heading */}
            <Box sx={{ mb: 4 }}>
                <Typography component="span" sx={{ fontSize: '0.8rem', fontWeight: 900, letterSpacing: '0.16em', textTransform: 'uppercase', color: ORANGE, display: 'block', mb: 1.25 }}>
                    {d.eyebrow}
                </Typography>
                <Typography component="h2" sx={{ fontSize: { xs: '1.85rem', md: '2.4rem' }, fontWeight: 900, letterSpacing: '-0.025em', mb: 1.5, lineHeight: 1.15, color: NAVY }}>
                    {d.title}
                </Typography>
                <Typography component="p" sx={{ fontSize: { xs: '1rem', md: '1.1rem' }, fontWeight: 500, lineHeight: 1.6, color: '#64748B', maxWidth: '760px' }}>
                    {d.subtitle}
                </Typography>
            </Box>

            <motion.div variants={container} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-50px' }}>

                {/* Refund tier cards */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2.5, mb: 4 }}>
                    {tiers.map((tier, idx) => {
                        const v = VARIANTS[tier.variant];
                        return (
                            <motion.div variants={item} key={idx} style={{ height: '100%' }}>
                                <Box
                                    sx={{
                                        height: '100%',
                                        minHeight: 230,
                                        p: 3.25,
                                        borderRadius: '24px',
                                        background: v.bg,
                                        color: v.color,
                                        border: v.border,
                                        boxShadow: v.shadow,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        transition: 'transform 0.3s ease',
                                        '&:hover': { transform: 'translateY(-6px)' },
                                    }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                                        <Box sx={{ width: 44, height: 44, borderRadius: '12px', background: v.iconBg, color: v.iconColor, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            {React.createElement(TIER_ICONS[tier.icon] || FaRegCalendarAlt, { size: '18px' })}
                                        </Box>
                                        <RefundRing percent={tier.percent} v={v} />
                                    </Box>

                                    <Box>
                                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: v.label, mb: 0.75 }}>
                                            {tier.label}
                                        </Typography>
                                        <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, lineHeight: 1.3, color: v.color }}>
                                            {tier.period}
                                        </Typography>
                                    </Box>
                                </Box>
                            </motion.div>
                        );
                    })}
                </Box>

                {/* Policy points */}
                <motion.div variants={item}>
                    <Box sx={{ p: { xs: 3, md: 3.5 }, borderRadius: '20px', background: '#ffffff', border: '1px solid #E8EDF5' }}>
                        <Typography sx={{ fontWeight: 800, color: NAVY, fontSize: '1.1rem', mb: 2.5 }}>
                            {d.pointsHeading}
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: { xs: 2, sm: 2.5 } }}>
                            {points.map((p, idx) => (
                                <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                                    <Box sx={{ flexShrink: 0, mt: '7px', width: 8, height: 8, borderRadius: '50%', background: ORANGE }} />
                                    <Typography sx={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.6, fontWeight: 500 }}>
                                        {p}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </motion.div>
            </motion.div>
        </Box>
    );
};

export default AdmissionRefundPolicy;
